import { ReactElement, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { planImageCardAssetsRootGuid } from '@constants'
import { DoubleSide, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import useGetPlanSettings from '@editor/hooks/requests/useGetPlanSettings'
import useSceneLineMarker from '@editor/hooks/useSceneLineMarker'
import { ThreeEvent } from '@react-three/fiber'
import { usePlanImageCardStore } from '@editorStores'
import { computePlanImageCardLayoutDimensions } from 'src/domain/planImageCards/layout'
import type { PlanImageCard } from 'src/domain/planImageCards/types'
import getFileUrl from 'src/domain/utils/getFileUrl'
import SceneLineMarker from '../SceneLineMarker'
import Text3DThree from '../Text3DThree'
import PlanImageCardImagePlane from './PlanImageCardImagePlane'

interface Props {
  planImageCard: PlanImageCard
  selected: boolean
  isPlaceholder: boolean
  projectId: string
  /**
   * True while this card is being dragged. The frame is then drawn inline
   * instead of through `useSceneLineMarker` (see PlanTablePreview) to avoid the
   * per-frame global-store churn that makes the drag lag.
   */
  isDragging?: boolean
  onClick: () => void
  /** Fires when a scene click misses this card (used to deselect the active card). */
  onPointerMissed?: (event: MouseEvent) => void
}

const PlanImageCardPreview = ({
  planImageCard,
  selected,
  isPlaceholder,
  projectId,
  isDragging = false,
  onClick,
  onPointerMissed,
}: Props): ReactElement => {
  const theme = useTheme()
  const { data: planSettings } = useGetPlanSettings()
  const planScale = planSettings?.scale ?? 100
  const imageContentVersion = usePlanImageCardStore(
    state => state.imageContentVersions[planImageCard.id] ?? 0,
  )
  const setStoreImageAspectRatio = usePlanImageCardStore(state => state.setImageAspectRatio)
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null)

  useEffect(() => {
    setImageAspectRatio(null)
  }, [imageContentVersion, isPlaceholder, planImageCard.id])

  // Report the loaded aspect ratio both to local layout and to the store, so the
  // controller can anchor the HUD at the card's real bottom edge.
  const handleAspectRatioKnown = useCallback(
    (aspectRatio: number) => {
      setImageAspectRatio(aspectRatio)
      setStoreImageAspectRatio(planImageCard.id, aspectRatio)
    },
    [planImageCard.id, setStoreImageAspectRatio],
  )

  const { width, totalHeight, titleBandHeight } = useMemo(
    () =>
      computePlanImageCardLayoutDimensions(planImageCard, {
        planScale,
        imageAspectRatio,
      }),
    [imageAspectRatio, planImageCard, planScale],
  )

  const z = planImageCard.position.z
  const x0 = planImageCard.position.x
  const yTop = planImageCard.position.y
  const yBottom = yTop - totalHeight
  const x1 = x0 + width

  const frameLines = useMemo(
    () => [
      [
        { x: x0, y: yTop, z },
        { x: x1, y: yTop, z },
      ],
      [
        { x: x1, y: yTop, z },
        { x: x1, y: yBottom, z },
      ],
      [
        { x: x1, y: yBottom, z },
        { x: x0, y: yBottom, z },
      ],
      [
        { x: x0, y: yBottom, z },
        { x: x0, y: yTop, z },
      ],
    ],
    [x0, x1, yBottom, yTop, z],
  )

  const borderColor = selected
    ? (theme.palette.primary.main as string)
    : isPlaceholder
    ? (theme.palette.warning.main as string)
    : (theme.palette.info.main as string)

  useSceneLineMarker({
    lines: frameLines,
    // While dragging, the frame is rendered inline (below) instead, to avoid the
    // per-frame global-store churn of the marker hook.
    enabled: !isDragging,
    color: borderColor,
    lineWidth: selected ? 3 : 2,
    dashed: isPlaceholder,
  })

  const titlePosition = useMemo(
    () => new Vector3(x0 + width / 2, yTop - titleBandHeight / 2, z + 0.001),
    [titleBandHeight, width, x0, yTop, z],
  )

  const hitTargetCenter = useMemo(
    () => new Vector3(x0 + width / 2, yTop - totalHeight / 2, z),
    [totalHeight, width, x0, yTop, z],
  )

  const imageUrl = useMemo(() => {
    const baseUrl = getFileUrl(
      { guid: planImageCard.fileGuid } as FileItem,
      projectId,
      planImageCardAssetsRootGuid,
    )
    return imageContentVersion > 0 ? `${baseUrl}?v=${imageContentVersion}` : baseUrl
  }, [imageContentVersion, planImageCard.fileGuid, projectId])

  const handleSelect = (event: ThreeEvent<MouseEvent>): void => {
    event.stopPropagation()
    onClick()
  }

  return (
    <group onPointerMissed={onPointerMissed}>
      <mesh
        position={hitTargetCenter}
        onClick={handleSelect}
        onPointerOver={event => {
          event.stopPropagation()
          document.body.style.cursor = 'pointer'
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'auto'
        }}
      >
        <planeGeometry args={[Math.max(width, 0.05), Math.max(totalHeight, 0.05)]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} side={DoubleSide} />
      </mesh>

      {isDragging && (
        <SceneLineMarker
          lines={frameLines}
          color={borderColor}
          lineWidth={selected ? 3 : 2}
          dashed={isPlaceholder}
        />
      )}

      {!isPlaceholder && (
        <Suspense fallback={null}>
          <PlanImageCardImagePlane
            key={`${planImageCard.id}-${imageContentVersion}`}
            url={imageUrl}
            x0={x0}
            yTop={yTop}
            titleBandHeight={titleBandHeight}
            totalHeight={totalHeight}
            width={width}
            z={z}
            onAspectRatioKnown={handleAspectRatioKnown}
          />
        </Suspense>
      )}

      <Text3DThree
        textLines={
          isPlaceholder
            ? ['Kein Bild', planImageCard.name]
            : [planImageCard.titleLines.join(' · ') || planImageCard.name]
        }
        color={theme.palette.text.primary}
        backgroundColor={theme.palette.background.paper}
        borderColor={borderColor}
        borderWidth={0.001}
        position={titlePosition}
        scale={planScale / 100 / 10}
        anchorLocation="center"
        onClick={handleSelect}
      />
    </group>
  )
}

export default PlanImageCardPreview
